import { useQuery } from '@tanstack/react-query';
import { dashboard, isForbidden } from '@cwos/api-client';
import type { dashboard as DashboardNS } from '@cwos/api-client';
import { Pill, Refused, SkeletonSet, type Column } from '@cwos/ui-admin';
import { useAnalyticsControls } from '../AnalyticsContext.js';
import { periodRangeLabel, weekShortLabel } from '../analyticsRange.js';
import { docStateLabel, docStateTone } from '../../dashboardLogic.js';
import { LineChart } from '../charts.js';
import { TrendFailure } from '../drilldown.js';
import { trendPoints } from '../disclosure.js';
import {
  DrilldownTable,
  Panel,
  Stat,
  StatGrid,
  StalenessStrip,
  type QueryLike,
} from '../shared.js';
import { LoadFailure } from '../../../LoadFailure.js';

type OpenDocument = DashboardNS.OpenDocument;

/**
 * THE ONE ANALYTICS VIEW BEHIND THE ROW-LEVEL KEY.
 *
 * Both of its person-bearing reads — `GET /api/dashboard/documentation` and
 * `/documentation/open` — are in the API's `ROW_LEVEL_ROUTES`, so an
 * `analytics:read`-only reader is refused at the door of the FIRST one and
 * nothing on this view survives it. `AnalyticsShell` therefore does not offer
 * the tab to a reader without `dashboard:read` (`ROW_LEVEL_VIEWS` there names
 * this route and the API file that classifies it).
 *
 * THE REFUSAL BRANCH BELOW IS NOT REDUNDANT AND MUST NOT BE REMOVED. Hiding a
 * tab is not a permission check: a deep link with `?view=documentation` still
 * mounts this component, and the client's flattened key list cannot see a
 * program-scoped `dashboard:read` that the server will refuse anyway. The
 * server stays the authority; this is where its answer is rendered.
 */
export function DocumentationView() {
  const { filterQuery, apiQuery } = useAnalyticsControls();
  const summary = useQuery({
    queryKey: ['dashboard', 'documentation', filterQuery],
    queryFn: () => dashboard.documentation(filterQuery),
  });

  /**
   * THE DEPENDENT READS WAIT FOR THE SUMMARY'S ANSWER.
   *
   * Both of these hang off the summary on screen — the refusal branch below
   * returns before either panel renders — so firing them alongside it bought
   * nothing on this view but concurrency. What that concurrency did on a
   * `?view=documentation` deep link from a reader the summary refuses was
   * issue two more requests whose answers can never be shown: `/open` is in
   * the same `ROW_LEVEL_ROUTES` list and 403s identically, and the trend,
   * though aggregate-readable, renders inside a view that has already been
   * answered with `<Refused>`.
   *
   * The gate is the summary's ACTUAL OUTCOME, not a client-side key check —
   * the server stays the authority on who reads what. `isSuccess` rather than
   * "not forbidden" because anything less waits for nothing: all three
   * requests would still leave in the same tick of a fresh mount.
   */
  const details = useQuery({
    queryKey: ['dashboard', 'documentation', 'open', filterQuery],
    queryFn: () => dashboard.documentationDetails(filterQuery),
    enabled: summary.isSuccess,
  });

  const trend = useQuery({
    // The trend follows the period picker; the summary and queue above are "now".
    queryKey: ['analytics', 'documentation', 'trend', apiQuery],
    queryFn: () => dashboard.analyticsDocumentationTrend(apiQuery),
    enabled: summary.isSuccess,
  });

  // A 403 is an answer. It was taking the failure branch and getting a retry
  // button that cannot change it.
  if (summary.isError && isForbidden(summary.error)) {
    return (
      <Refused
        heading="This view is not shown to you"
        message="Your account does not have access to the figures behind it. Nothing has been left out of them — they are not yours to see."
      />
    );
  }
  if (summary.isLoading) return <SkeletonSet surface="table" rows={3} />;
  if (summary.isError && !summary.data) {
    return (
      <LoadFailure
        error={summary.error}
        message="We couldn’t load documentation status just now, so read this screen as unknown rather than empty."
        onRetry={() => void summary.refetch()}
      />
    );
  }
  const d = summary.data;
  // `return null` rendered a BLANK VIEW — the reader saw a page with nothing on
  // it and no reason given. A query that has not answered is pending.
  if (!d) return <SkeletonSet surface="table" rows={3} />;

  const columns: Column<OpenDocument>[] = [
    {
      key: 'client',
      header: 'Client',
      render: (r) => (
        <span>
          <span style={{ fontWeight: 600 }}>{r.clientLabel}</span>
          {r.workerLabel ? <span className="rn-muted"> · {r.workerLabel}</span> : null}
        </span>
      ),
    },
    {
      key: 'state',
      header: 'State',
      render: (r) => (
        <Pill variant={docStateTone(r.isLate)} dot>
          {docStateLabel(r.isLate)}
        </Pill>
      ),
    },
    {
      key: 'age',
      header: 'Open for',
      render: (r) => (r.lagDays !== null ? `${r.lagDays}d` : '—'),
      numeric: true,
    },
    {
      key: 'incomplete',
      header: 'Incomplete',
      render: (r) => String(r.incompleteCount),
      numeric: true,
    },
  ];

  return (
    <div className="an-stack">
      <StatGrid>
        <Stat label="Open" value={String(d.totalOpen)} />
        <Stat label="< 24 h" value={String(d.byAge.under24h)} />
        <Stat label="1–3 days" value={String(d.byAge.day1to3)} />
        <Stat label="4–7 days" value={String(d.byAge.day4to7)} />
        <Stat label="> 7 days" value={String(d.byAge.over7d)} />
      </StatGrid>

      <StalenessStrip staleness={d.staleness} />

      <DocTrendPanel trend={trend} />

      <Panel title="Open documentation" subtitle="by visit week, ordered by age">
        <DrilldownTable
          rows={details.data}
          isLoading={details.isLoading}
          isError={details.isError}
          error={details.error}
          onRetry={() => void details.refetch()}
          columns={columns}
          rowKey={(r) => r.docStatusId}
          emptyHeading="Nothing open"
          emptyMessage="Documentation is caught up."
        />
      </Panel>
    </div>
  );
}

function DocTrendPanel({ trend }: { trend: QueryLike<DashboardNS.DocumentationTrend> }) {
  if (trend.isError && !trend.data) {
    return (
      <TrendFailure
        error={trend.error}
        message="We couldn’t load the documentation trend just now."
        onRetry={() => void trend.refetch()}
      />
    );
  }
  const t = trend.data;
  // No answer and no error is PENDING, not absent: this query now waits on the
  // summary's outcome, and react-query reports the not-yet-enabled interval as
  // neither loading nor errored. `return null` here would render a blank band.
  if (!t) return <SkeletonSet surface="band" />;
  const labels = t.weeks.map(weekShortLabel);
  return (
    <Panel
      title="Open vs overdue"
      subtitle={`by the week the visit occurred · ${periodRangeLabel(t.period)}`}
    >
      <LineChart
        labels={labels}
        coverage={t.coverage}
        series={[
          { name: 'Open', color: 'var(--chart-2)', points: trendPoints(t.open) },
          { name: 'Overdue', color: 'var(--color-look)', points: trendPoints(t.overdue) },
        ]}
      />
    </Panel>
  );
}
